/**
 * useTrustedDeviceRevocationListener — kick this browser to /login
 * within ~1 second if the trusted device that minted its AAL2 session
 * gets revoked from another browser.
 *
 * Why this exists:
 *   JWT_EXPIRY is 300s (see prod auth env), so without an active push
 *   a revoked session would survive up to 5 minutes — long enough for
 *   a stolen-laptop scenario to do damage. Postgres-changes events on
 *   `public.trusted_devices` are RLS-filtered, so we only ever see
 *   updates to OUR OWN devices. When we see `revoked_at` get set for
 *   the device id the access-token hook stamped into our JWT
 *   (`app_metadata.trusted_device_id`), we sign out immediately.
 *
 *   Browsers signed in *without* a trusted-device upgrade (fresh
 *   password + MFA, no remember-me) don't have a `trusted_device_id`
 *   in their JWT, so they correctly ignore other browsers' revoke
 *   events on the same user.
 *
 * Why we don't sign out on EVERY revoke event:
 *   A user revoking their *old* laptop while sitting at their desktop
 *   should NOT lose their desktop session. The match-on-device-id
 *   check is what makes the behaviour feel correct instead of
 *   trigger-happy.
 *
 * Mount once, near the top of the authenticated tree (inside
 * AuthProvider). Subscription tears down on sign-out via the
 * useEffect cleanup; re-subscribes when a new access token comes
 * in (e.g. another sign-in on the same tab).
 */
import { useEffect, useRef } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../../../lib/supabase";

interface TrustedDeviceRow {
  id: string;
  revoked_at: string | null;
}

interface RealtimePayload {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: TrustedDeviceRow | null;
  old: TrustedDeviceRow | null;
}

// The supabase-js v2 typings for `.on('postgres_changes', …)` resolve
// to a different overload than the runtime API actually uses, which
// trips TS even though every other call site in this repo uses the
// same incantation at runtime. Cast the channel through this loose
// shape so we can subscribe without `as any` peppered everywhere.
interface ChannelWithPgChanges {
  on(
    event: "postgres_changes",
    config: {
      event: "INSERT" | "UPDATE" | "DELETE" | "*";
      schema: string;
      table: string;
      filter?: string;
    },
    handler: (payload: RealtimePayload) => void,
  ): ChannelWithPgChanges;
  subscribe(): RealtimeChannel;
}

/**
 * Decode the access token's payload and pull out the
 * trusted_device_id stamped by custom_access_token_hook. Returns null
 * for sessions that didn't go through the trusted-device upgrade path
 * (i.e. fresh password + MFA sign-ins, kiosk-mode, etc.) — those
 * browsers should ignore revoke events entirely.
 */
function readTrustedDeviceIdFromAccessToken(
  accessToken: string | null,
): string | null {
  if (!accessToken) return null;
  try {
    const segment = accessToken.split(".")[1] ?? "";
    const padded =
      segment.replace(/-/g, "+").replace(/_/g, "/") +
      "=".repeat((4 - (segment.length % 4)) % 4);
    const bin = atob(padded);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const claims = JSON.parse(new TextDecoder("utf-8").decode(bytes)) as {
      app_metadata?: { trusted_device_id?: string };
    };
    return claims.app_metadata?.trusted_device_id ?? null;
  } catch {
    return null;
  }
}

export function useTrustedDeviceRevocationListener(
  accessToken: string | null | undefined,
): void {
  // Track the device id we've decoded from the current JWT. Using a
  // ref so the inline subscription handler always reads the latest
  // value without needing to be re-subscribed on every token refresh
  // (token refreshes keep the same trusted_device_id, but the access
  // token *string* changes — re-subscribing on every refresh would
  // briefly disconnect and we'd miss a revoke event that landed in
  // the gap).
  const myDeviceIdRef = useRef<string | null>(null);

  useEffect(() => {
    myDeviceIdRef.current = readTrustedDeviceIdFromAccessToken(
      accessToken ?? null,
    );
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    const channel = (
      supabase.channel(
        "trusted-device-revocations",
      ) as unknown as ChannelWithPgChanges
    )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "trusted_devices",
        },
        (payload: RealtimePayload) => {
          // Postgres-changes is RLS-filtered, so we already know this
          // row belongs to us. We only act on the revoked_at NULL →
          // not-NULL transition AND only when the just-revoked device
          // is THIS browser's device.
          const wasActive = payload.old?.revoked_at == null;
          const isRevoked = payload.new?.revoked_at != null;
          if (!wasActive || !isRevoked) return;

          const revokedId = payload.new?.id;
          const myId = myDeviceIdRef.current;
          if (!revokedId || !myId) return;
          if (revokedId !== myId) return;

          // Fire-and-forget signOut + redirect. Local scope so the
          // refresh token on this browser is invalidated; global
          // scope would also drop other devices, which would be
          // wrong here (the server already nuked exactly the
          // sessions tied to THIS device via fn_revoke_trusted_device).
          void supabase.auth.signOut({ scope: "local" }).finally(() => {
            window.location.replace("/login");
          });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
    // We intentionally only re-subscribe when the user signs out and
    // in again — accessToken being truthy means "we have a session";
    // token-refresh updates the access_token string but the channel
    // can survive that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken == null ? null : "subscribed"]);
}
